// COMPLETE FILE: src/hooks/useWebSocket.js
// Fixed version with proper ESLint dependency handling

import { useState, useEffect, useRef, useCallback } from 'react';
import { wsManager } from '../utils/websocket-manager';
import { wsConnectionTracker } from '../utils/websocket-connection-tracker';
import { config } from '../config';
import { getWsUrl, ensureFreshToken } from '../utils/dashboardContext';

export const useWebSocket = (enabled = !config.DISABLE_WEBSOCKET) => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(enabled ? 'connecting' : 'disabled');
  const [connectionQuality, setConnectionQuality] = useState('unknown');
  const [error, setError] = useState(null);
  const [connectedClients, setConnectedClients] = useState(0);
  const [ownClients, setOwnClients] = useState(0); // THIS login's sockets (duplicate-tab warning)
  
  const wsRef = useRef(null);
  const subscriptionsRef = useRef(new Map());
  const serverToLocalIdMap = useRef(new Map()); // Map server subscription IDs to local IDs
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = config.MAX_RECONNECT_ATTEMPTS || 5;
  const connectionTimeoutRef = useRef(null);
  const isManualCloseRef = useRef(false);
  const lastPingRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const connectionConfirmedRef = useRef(false); // Track connection confirmation
  
  // Store the active WebSocket instance globally to survive React StrictMode
  const getActiveWs = useCallback(() => { // eslint-disable-line no-unused-vars
    return wsRef.current;
  }, []);
  
  // Function to send a subscription when WebSocket is ready
  const sendWhenReady = useCallback((subscriptionId, subscription) => { // eslint-disable-line no-unused-vars
    const maxAttempts = 20;
    let attempts = 0;
    
    const trySend = () => {
      attempts++;
      const ws = wsRef.current;
      
      if (ws && ws.readyState === 1 && !subscription.sent) { // 1 = WebSocket.OPEN
        try {
          ws.send(JSON.stringify({
            type: subscription.type,
            payload: subscription.payload
          }));
          subscription.sent = true;
          console.log(`📡 Sent subscription after ${attempts} attempts: ${subscription.type} (${subscriptionId})`);
          return true;
        } catch (error) {
          console.error(`Failed to send subscription ${subscriptionId}:`, error);
          return false;
        }
      } else if (attempts < maxAttempts && !subscription.sent) {
        setTimeout(trySend, 50);
      } else if (!subscription.sent) {
        console.warn(`📡 Failed to send subscription after ${maxAttempts} attempts: ${subscriptionId}`);
      }
      return false;
    };
    
    trySend();
  }, []);
  
  // iOS Safari detection
  const isIOSSafari = useCallback(() => {
    const ua = navigator.userAgent;
    return /iPad|iPhone|iPod/.test(ua) && /Safari/.test(ua) && !/Chrome|CriOS|FxiOS/.test(ua);
  }, []);

  // Tenant-scoped WebSocket URL: wss://<host>/ws?tenant=<slug>[&token=<jwt>].
  // (Replaces the source's single-host derivation — the shared /ws server
  // scopes every connection by tenant.)
  const getWebSocketUrl = useCallback(() => getWsUrl(), []);

  // Enhanced ping system
  const sendPing = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      lastPingRef.current = Date.now();
      const pingMessage = { type: 'ping', timestamp: lastPingRef.current };
      if (config.LOG_PING_PONG) {
        console.log('🏓 Sending ping to server:', new Date(lastPingRef.current).toISOString());
      }
      wsRef.current.send(JSON.stringify(pingMessage));
    }
  }, []);

  // Handle pong responses for latency calculation
  const handlePong = useCallback((timestamp) => {
    if (lastPingRef.current) {
      const latency = Date.now() - lastPingRef.current;
      
      if (latency < 100) {
        setConnectionQuality('excellent');
      } else if (latency < 300) {
        setConnectionQuality('good');
      } else if (latency < 1000) {
        setConnectionQuality('fair');
      } else {
        setConnectionQuality('poor');
      }
      
      if (config.LOG_PING_PONG) {
        console.log(`🏓 WebSocket ping: ${latency}ms`);
      }
    }
  }, []);
  
  // Enhanced message handling
  const handleMessage = useCallback((message) => {
    const { type, subscriptionId, data } = message;
    
    switch (type) {
      case 'connection':
        console.log('📡 WebSocket connection established:', message.message);
        console.log('🆔 Your client ID:', message.clientId);
        window._wsClientId = message.clientId; // Store for debugging
        connectionConfirmedRef.current = true; // Mark connection as confirmed
        setConnectionQuality('good');
        
        // Check if connection message contains client count
        if (message.connectedClients !== undefined) {
          setConnectedClients(message.connectedClients);
          console.log('👥 Connected clients from connection message:', message.connectedClients);
        }
        
        // NOW it's safe to register with WebSocket manager and process subscriptions
        console.log('📡 Connection confirmed - registering WebSocket and processing subscriptions');
        wsManager.setWebSocket(wsRef.current);
        
        // Resubscribe to all existing subscriptions
        resubscribeAll();
        
        // Process any pending subscriptions
        setTimeout(() => {
          console.log('📡 Processing queued subscriptions after connection confirmation');
          console.log('📡 WebSocket state:', wsRef.current?.readyState, 'Subscription count:', subscriptionsRef.current.size);
          
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            let sentCount = 0;
            for (const [subscriptionId, subscription] of subscriptionsRef.current.entries()) {
              if (!subscription.sent) {
                try {
                  wsRef.current.send(JSON.stringify({
                    type: subscription.type,
                    payload: subscription.payload
                  }));
                  subscription.sent = true;
                  sentCount++;
                  console.log(`📡 Sent queued subscription: ${subscription.type} (${subscriptionId})`);
                } catch (error) {
                  console.error(`Failed to send queued subscription ${subscriptionId}:`, error);
                }
              }
            }
            console.log(`📡 Sent ${sentCount} queued subscriptions`);
            
            // Also process manager's pending subscriptions
            wsManager.processPendingSubscriptions();
          }
        }, 100);
        break;
        
      case 'subscription_created': {
        console.log(`✅ Subscription created: ${subscriptionId} (${message.dataType})`);
        // Deterministic mapping: the server echoes our clientSubscriptionId.
        const echoed = message.clientSubscriptionId;
        if (echoed && subscriptionsRef.current.has(echoed)) {
          const subscription = subscriptionsRef.current.get(echoed);
          subscription.serverSubscriptionId = subscriptionId;
          serverToLocalIdMap.current.set(subscriptionId, echoed);
          console.log(`📡 Mapped server subscription ${subscriptionId} to local ${echoed} (echoed)`);
          break;
        }
        // Fallback (older server without the echo): first unmapped of that type.
        for (const [localId, subscription] of subscriptionsRef.current.entries()) {
          if (subscription.type.includes(message.dataType) && !subscription.serverSubscriptionId) {
            subscription.serverSubscriptionId = subscriptionId;
            serverToLocalIdMap.current.set(subscriptionId, localId);
            console.log(`📡 Mapped server subscription ${subscriptionId} to local ${localId}`);
            break;
          }
        }
        break;
      }
        
      case 'events_update':
      case 'event_detail_update':
      case 'meta_campaigns_update':
        // Find and call the appropriate callback using the server-to-local ID mapping
        const localSubscriptionId = serverToLocalIdMap.current.get(subscriptionId);
        if (localSubscriptionId && subscriptionsRef.current.has(localSubscriptionId)) {
          const subscription = subscriptionsRef.current.get(localSubscriptionId);
          console.log(`📥 Received ${type} for subscription ${localSubscriptionId} (server: ${subscriptionId})`);
          subscription.callback(data);
          subscription.lastUpdate = Date.now();
        } else {
          // This can happen after reconnection when server still has old subscriptions
          console.log(`📦 Received ${type} for unmapped subscription ${subscriptionId} (likely from previous connection)`);
          // Log more details to debug
          console.log('📊 Current subscription mappings:', {
            localSubscriptions: subscriptionsRef.current.size,
            serverMappings: serverToLocalIdMap.current.size,
            unmappedId: subscriptionId
          });
        }
        break;
        
      case 'error':
        console.error('WebSocket subscription error:', message.message);
        setError(`Subscription error: ${message.message}`);
        break;
        
      case 'status':
        // Handle server status updates including connected clients count
        if (message.connectedClients !== undefined) {
          setConnectedClients(message.connectedClients);
        }
        if (message.ownClients !== undefined) {
          setOwnClients(message.ownClients);
        }
        break;
        
      case 'ping':
        // Server ping received - keeping connection alive
        // Note: Server doesn't expect pong response, so we just acknowledge it
        if (config.LOG_PING_PONG) {
          console.log('🏓 Ping received from server');
        }
        break;
        
      case 'pong':
        handlePong(message.timestamp);
        break;
        
      case 'server_shutdown':
        console.warn('🛑 Server is shutting down:', message.message);
        setError('Server is restarting - will reconnect automatically');
        break;
        
      default:
        console.warn('Unknown WebSocket message type:', type, message);
        // Check if any unhandled message contains client count
        if (message.connectedClients !== undefined) {
          setConnectedClients(message.connectedClients);
          console.log('👥 Connected clients from unhandled message type:', type, message.connectedClients);
        }
    }
  }, [handlePong]);

  // Resubscribe to all active subscriptions
  const resubscribeAll = useCallback(() => {
    // Clear any old server subscription mappings before resubscribing
    serverToLocalIdMap.current.clear();
    console.log('🧹 Cleared old server subscription mappings before resubscribing');
    
    const subscriptionCount = subscriptionsRef.current.size;
    if (subscriptionCount === 0) {
      console.log('📡 No subscriptions to restore');
      return;
    }
    
    console.log(`📡 Restoring ${subscriptionCount} subscriptions...`);
    console.log('📡 WebSocket state:', wsRef.current?.readyState === WebSocket.OPEN ? 'OPEN' : 'NOT OPEN');
    
    // Reset sent flag and server subscription ID on all subscriptions before resubscribing
    for (const subscription of subscriptionsRef.current.values()) {
      subscription.sent = false;
      subscription.serverSubscriptionId = null;
    }
    
    for (const [subscriptionId, subscription] of subscriptionsRef.current.entries()) {
      const message = {
        type: subscription.type,
        payload: subscription.payload
      };
      
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        try {
          wsRef.current.send(JSON.stringify(message));
          subscription.sent = true;
          console.log(`🔄 Restored subscription: ${subscription.type} (${subscriptionId})`);
        } catch (error) {
          console.error(`Failed to restore subscription ${subscriptionId}:`, error);
        }
      } else {
        console.log(`📡 Cannot restore subscription ${subscriptionId} - WebSocket not open`);
      }
    }
  }, []);

  // Enhanced reconnection logic (defined before connect to avoid circular dependency)
  const attemptReconnect = useCallback(() => {
    if (!enabled) {
      console.log('🔌 Reconnection skipped - WebSocket disabled');
      return;
    }

    // NEVER give up. The old behavior stopped after maxReconnectAttempts and
    // set 'failed' — a 2-minute network blip permanently killed realtime on a
    // days-open wall display. Past maxReconnectAttempts the backoff simply
    // sits at its 30s ceiling and keeps trying forever; visibility/online
    // handlers reset the counter so recovery is immediate when the network is
    // actually back.

    // Clear any existing reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    reconnectAttemptsRef.current++;

    // Enhanced backoff strategy with jitter (capped at 30s, Math.pow safely
    // saturates to Infinity → min() keeps it at 30000 for large counters)
    const baseDelay = isIOSSafari() ? 2000 : 1000;
    const exponentialDelay = Math.min(baseDelay * Math.pow(1.5, reconnectAttemptsRef.current - 1), 30000);
    // Add jitter to prevent thundering herd
    const jitter = Math.random() * 1000;
    const delay = exponentialDelay + jitter;

    console.log(`🔄 Reconnecting in ${Math.round(delay)}ms (attempt ${reconnectAttemptsRef.current})`);
    setConnectionStatus('reconnecting');
    setError(`Reconnecting... (attempt ${reconnectAttemptsRef.current})`);
    
    reconnectTimeoutRef.current = setTimeout(() => {
      if (enabled) {
        console.log('🔄 Executing reconnect...');
        // Clear the websocket reference to ensure fresh connection
        if (wsRef.current) {
          wsRef.current.close();
          wsRef.current = null;
        }
        // Reset manual close flag
        isManualCloseRef.current = false;
        // Directly call connect instead of triggering through status change
        connect();
      }
    }, delay);
  }, [enabled, isIOSSafari]);

  // WebSocket connection with enhanced error handling
  const connect = useCallback(async () => {
    if (!enabled) {
      console.log('🔌 WebSocket connection disabled');
      setConnectionStatus('disabled');
      return;
    }

    // Generate unique connection ID
    const connectionId = `conn_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Check if we can start a new connection
    // For iOS Safari, force connection if needed due to Safari's aggressive connection handling
    const forceConnect = isIOSSafari() && wsConnectionTracker.hasActiveConnection();
    
    if (!wsConnectionTracker.startConnection(connectionId, forceConnect)) {
      console.log('🔌 Connection already in progress or active');
      // On iOS Safari, log more details
      if (isIOSSafari()) {
        console.log('📱 iOS Safari - Connection tracker state:', {
          hasActive: wsConnectionTracker.hasActiveConnection(),
          isConnecting: wsConnectionTracker.isConnecting,
          activeState: wsConnectionTracker.activeConnection?.readyState
        });
      }
      // Don't dead-end: if the tracker refused us (rapid-reconnect guard /
      // stale isConnecting) and there's genuinely no open socket, try again
      // shortly — otherwise status can sit at 'reconnecting' forever with no
      // timer armed (the "manual reconnect does nothing" bug).
      if (!wsConnectionTracker.hasActiveConnection() && !reconnectTimeoutRef.current) {
        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectTimeoutRef.current = null;
          connect();
        }, 1500);
      }
      return;
    }

    try {
      // Clear any existing connection
      if (wsRef.current) {
        isManualCloseRef.current = true;
        wsRef.current.close();
        wsRef.current = null;
      }
      
      setConnectionStatus('connecting');
      setError(null);
      setConnectionQuality('unknown');
      isManualCloseRef.current = false;
      connectionConfirmedRef.current = false; // Reset connection confirmation

      // Refresh the token first if it's expired/near-expiry, so the upgrade
      // (validated server-side at connect time) carries a valid token. No-op
      // when the token is comfortably fresh.
      await ensureFreshToken();

      const wsUrl = getWebSocketUrl();
      console.log('🔌 Connecting to WebSocket:', wsUrl);
      console.log('📱 iOS Safari detected:', isIOSSafari());
      
      // Clear any existing timeout
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
      }
      
      // iOS Safari needs longer timeout
      const connectionTimeout = config.CONNECTION_TIMEOUT || (isIOSSafari() ? 15000 : 10000);
      
      // Create the WebSocket first
      console.log(`🔌 Creating WebSocket connection to: ${wsUrl}`);
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      console.log('🔌 WebSocket object created, readyState:', ws.readyState);
      
      // Register with connection tracker
      wsConnectionTracker.setConnection(ws, connectionId);
      
      // Set connection timeout
      connectionTimeoutRef.current = setTimeout(() => {
        if (ws && ws.readyState === WebSocket.CONNECTING) {
          console.warn('⏰ WebSocket connection timeout');
          setConnectionStatus('error');
          setError('Connection timeout - server may be unavailable');
          // ws.close() fires onclose, which schedules the reconnect — calling
          // attemptReconnect() here too double-counted every failed attempt.
          ws.close();
          wsConnectionTracker.endConnection(connectionId);
        }
      }, connectionTimeout);
      
      ws.onopen = () => {
        console.log('✅ WebSocket connected successfully');
        console.log('✅ WebSocket readyState:', ws.readyState); // Should be 1 (OPEN)
        
        // Clear connection timeout
        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }
        
        // Clear any pending reconnect attempts
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
        
        setIsConnected(true);
        setConnectionStatus('connected');
        setError(null);
        reconnectAttemptsRef.current = 0;
        
        // Start ping monitoring - more aggressive to prevent timeout
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }
        pingIntervalRef.current = setInterval(sendPing, 25000); // Every 25 seconds (well before 60s timeout)
        
        // DO NOT resubscribe or process subscriptions here
        // Wait for the connection confirmation message from server
        console.log('📡 WebSocket open - waiting for connection confirmation before processing subscriptions');
      };
      
      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          
          // Log messages for debugging (skip ping/pong unless enabled)
          if ((message.type !== 'ping' && message.type !== 'pong') || config.LOG_PING_PONG) {
            console.log('🔵 WebSocket message received:', message.type, message);
          }
          
          handleMessage(message);
        } catch (err) {
          console.error('Failed to parse WebSocket message:', err);
          console.error('Raw message:', event.data);
          setError('Invalid message format received');
        }
      };
      
      ws.onclose = (event) => {
        console.log('🔌 WebSocket disconnected:', event.code, event.reason);
        console.log('🔌 Close event details:', {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
          timestamp: new Date().toISOString(),
          isManualClose: isManualCloseRef.current
        });
        
        // Clear connection timeout and ping interval
        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }
        
        setIsConnected(false);
        setConnectionQuality('unknown');
        
        // Enhanced close code handling
        if (event.code === 1000 && !isManualCloseRef.current) {
          // Clean close but not manual - likely server restart
          setConnectionStatus('disconnected');
          setError('Server disconnected - reconnecting...');
        } else if (event.code === 1000) {
          setConnectionStatus('disconnected');
        } else if (event.code === 1006) {
          setConnectionStatus('abnormal_closure');
          setError('Connection lost unexpectedly');
        } else if (event.code >= 4000) {
          setConnectionStatus('server_error');
          setError('Server rejected connection');
        } else {
          setConnectionStatus('disconnected');
        }
        
        // Always attempt reconnect unless it was a manual close or we're disabled
        if (!isManualCloseRef.current && enabled) {
          console.log('🔌 Will attempt reconnect because: manual=', isManualCloseRef.current, 'code=', event.code, 'enabled=', enabled);
          attemptReconnect();
        } else {
          console.log('🔌 Not reconnecting: manual=', isManualCloseRef.current, 'code=', event.code, 'enabled=', enabled);
        }
        
        // Clear the websocket reference only after handling reconnection logic
        wsRef.current = null;
        // Also clear the manager's reference
        wsManager.clear();
        // Clear from connection tracker
        wsConnectionTracker.endConnection(connectionId);
        // Clear subscription mappings on disconnect
        serverToLocalIdMap.current.clear();
        console.log('🧹 Cleared server subscription mappings');
      };
      
      ws.onerror = (error) => {
        console.error('❌ WebSocket error:', error);
        
        // Clear timeouts
        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }
        
        // Enhanced error messages
        let errorMessage = 'WebSocket connection failed';
        if (isIOSSafari()) {
          if (window.location.protocol === 'https:' && ws.url?.startsWith('ws:')) {
            errorMessage = 'Mixed content error - refresh page and try again';
          } else {
            errorMessage = 'Network error - check connection and firewall';
          }
        } else {
          errorMessage = 'Unable to connect to server - check if server is running';
        }
        
        setError(errorMessage);
        setConnectionStatus('error');
        
        // Clear from connection tracker on error
        wsConnectionTracker.endConnection(connectionId);
      };
      
    } catch (err) {
      console.error('Failed to create WebSocket connection:', err);
      setError(`Connection failed: ${err.message}`);
      setConnectionStatus('error');
      wsConnectionTracker.endConnection(connectionId);
      if (enabled) {
        attemptReconnect();
      }
    }
  }, [getWebSocketUrl, isIOSSafari, enabled, sendPing, attemptReconnect, handleMessage]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Subscribe to events list
  const subscribeToEvents = useCallback((query, variables, callback) => {
    const subscriptionId = `events_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const subscription = {
      id: subscriptionId,
      type: 'subscribe_events',
      // clientSubscriptionId is echoed back in subscription_created so the
      // server↔local mapping is deterministic (the old first-unmapped-by-type
      // scan could bind event A's stream to event B's card).
      payload: { query, variables, clientSubscriptionId: subscriptionId },
      callback,
      createdAt: Date.now(),
      lastUpdate: null,
      sent: false
    };
    
    subscriptionsRef.current.set(subscriptionId, subscription);
    
    // Only send if WebSocket is ready AND connection is confirmed
    if (wsRef.current?.readyState === WebSocket.OPEN && connectionConfirmedRef.current) {
      try {
        wsRef.current.send(JSON.stringify({
          type: 'subscribe_events',
          payload: { query, variables, clientSubscriptionId: subscriptionId }
        }));
        subscription.sent = true;
        console.log('📡 Subscribed to events list');
      } catch (error) {
        console.error('Failed to send subscription:', error);
        subscriptionsRef.current.delete(subscriptionId);
        throw new Error('Failed to create subscription');
      }
    } else {
      console.log('📡 WebSocket not ready - events subscription queued (readyState:', wsRef.current?.readyState, 'confirmed:', connectionConfirmedRef.current, ')');
      // Add to manager for processing
      wsManager.addSubscription(subscriptionId, subscription);
    }
    
    // Return enhanced unsubscribe function
    return () => {
      const serverId = subscriptionsRef.current.get(subscriptionId)?.serverSubscriptionId;
      subscriptionsRef.current.delete(subscriptionId);
      if (serverId && wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'unsubscribe',
          payload: { subscriptionId: serverId }
        }));
      }
      console.log(`🔕 Unsubscribed from events (${subscriptionId})`);
    };
  }, []);
  
  // Subscribe to specific event details
  const subscribeToEventDetail = useCallback((eventId, timeslotId, callback) => {
    const subscriptionId = `event_detail_${eventId}_${Date.now()}`;
    
    const query = `
      query Event($id: ID!, $timeSlotIds: [String!]) {
        event(id: $id) {
          id
          slugParam
          capacity
          title
          waitlistCount
          nextFutureTimeSlot {
            id
            startAt
            endAt
          }
          rates(states: [ACTIVE, LOCKED, INACTIVE, HIDDEN], clientGroups: [EVERYWHERE]) {
            nodes {
              id
              name
              price
              displayPrice
              maxQuantity
              sortIndex
            }
          }
          timeSlots(ids: $timeSlotIds) {
            nodes {
              id
              ratesAvailability: ratesAvailablity(states: [ACTIVE, INACTIVE, LOCKED])
              timeSlotReport {
                totalGross
                totalFee
                totalNet
                ticketsByDate
                salesByDate
                ticketItemsSold
              }
            }
          }
        }
      }
    `;
    
    const subscription = {
      id: subscriptionId,
      type: 'subscribe_event_detail',
      payload: {
        query,
        variables: { id: eventId, timeSlotIds: [timeslotId] },
        clientSubscriptionId: subscriptionId
      },
      callback,
      eventId,
      createdAt: Date.now(),
      lastUpdate: null,
      sent: false
    };
    
    subscriptionsRef.current.set(subscriptionId, subscription);
    
    // Only send if WebSocket is ready AND connection is confirmed
    if (wsRef.current?.readyState === WebSocket.OPEN && connectionConfirmedRef.current) {
      try {
        wsRef.current.send(JSON.stringify({
          type: 'subscribe_event_detail',
          payload: subscription.payload
        }));
        subscription.sent = true;
        console.log(`📡 Subscribed to event detail: ${eventId}`);
      } catch (error) {
        console.error('Failed to send event detail subscription:', error);
        subscriptionsRef.current.delete(subscriptionId);
        throw new Error('Failed to create event detail subscription');
      }
    } else {
      console.log(`📡 WebSocket not ready - event detail subscription queued for ${eventId} (readyState:`, wsRef.current?.readyState, 'confirmed:', connectionConfirmedRef.current, ')');
      // Add to manager for processing
      wsManager.addSubscription(subscriptionId, subscription);
    }
    
    return () => {
      // The server only knows ITS subscription id — sending the local id
      // never unsubscribed anything (the server kept pushing until disconnect).
      const serverId = subscriptionsRef.current.get(subscriptionId)?.serverSubscriptionId;
      subscriptionsRef.current.delete(subscriptionId);
      if (serverId && wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'unsubscribe',
          payload: { subscriptionId: serverId }
        }));
      }
      console.log(`🔕 Unsubscribed from event detail: ${eventId} (${subscriptionId})`);
    };
  }, []);
  
  // Subscribe to Meta campaigns
  const subscribeToMetaCampaigns = useCallback((callback) => {
    const subscriptionId = `meta_campaigns_${Date.now()}`;
    
    const subscription = {
      id: subscriptionId,
      type: 'subscribe_meta_campaigns',
      payload: { clientSubscriptionId: subscriptionId },
      callback,
      createdAt: Date.now(),
      lastUpdate: null,
      sent: false
    };
    
    subscriptionsRef.current.set(subscriptionId, subscription);
    
    // Only send if WebSocket is ready AND connection is confirmed
    if (wsRef.current?.readyState === WebSocket.OPEN && connectionConfirmedRef.current) {
      try {
        wsRef.current.send(JSON.stringify({
          type: 'subscribe_meta_campaigns',
          payload: subscription.payload
        }));
        subscription.sent = true;
        console.log('📡 Subscribed to Meta campaigns');
      } catch (error) {
        console.error('Failed to send Meta campaigns subscription:', error);
        subscriptionsRef.current.delete(subscriptionId);
        throw new Error('Failed to create Meta campaigns subscription');
      }
    } else {
      console.log('📡 WebSocket not ready - Meta campaigns subscription queued (readyState:', wsRef.current?.readyState, 'confirmed:', connectionConfirmedRef.current, ')');
      // Add to manager for processing
      wsManager.addSubscription(subscriptionId, subscription);
    }
    
    return () => {
      const serverId = subscriptionsRef.current.get(subscriptionId)?.serverSubscriptionId;
      subscriptionsRef.current.delete(subscriptionId);
      if (serverId && wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'unsubscribe',
          payload: { subscriptionId: serverId }
        }));
      }
      console.log(`🔕 Unsubscribed from Meta campaigns (${subscriptionId})`);
    };
  }, []);
  
  // Enhanced disconnect with cleanup
  const disconnect = useCallback(() => {
    console.log('🔌 Manually disconnecting WebSocket...');
    isManualCloseRef.current = true;
    
    // Clear all timeouts and intervals
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }
    
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    
    if (wsRef.current) {
      wsRef.current.close(1000, 'Manual disconnect');
    }
    
    subscriptionsRef.current.clear();
    serverToLocalIdMap.current.clear();
    setIsConnected(false);
    setConnectionStatus(enabled ? 'disconnected' : 'disabled');
    setConnectionQuality('unknown');
    console.log('🔌 WebSocket disconnected manually');
  }, [enabled]);
  
  // Enhanced visibility change handling for mobile
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log('📱 App backgrounded');
        // Don't disconnect, but stop ping monitoring to save battery
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }
      } else {
        console.log('📱 App foregrounded');
        // Restart ping monitoring
        if (isConnected && !pingIntervalRef.current) {
          pingIntervalRef.current = setInterval(sendPing, 25000);
        }
        // Foreground = a fresh chance: reset the attempt counter (backoff
        // restarts fast) and reconnect if needed. NO attempts<max gate — that
        // gate is what left wall displays permanently dark after an outage.
        if (!isConnected) {
          reconnectAttemptsRef.current = 0;
          console.log('📱 Will attempt reconnection after foreground if still disconnected');
          setTimeout(() => {
            if (!isConnected) {
              console.log('📱 Attempting reconnection after foreground delay');
              connect();
            }
          }, 2000); // Increased delay to avoid race conditions
        }
      }
    };

    // Network came back (laptop lid, wifi blip, cell handoff) — reconnect now
    // instead of waiting out a 30s backoff tick.
    const handleOnline = () => {
      console.log('🌐 Browser online event');
      if (!isConnected) {
        reconnectAttemptsRef.current = 0;
        setTimeout(() => { if (!isConnected) connect(); }, 1000);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, [isConnected, connect, sendPing]);
  
  // Initialize connection on mount and when enabled state changes
  useEffect(() => {
    let connectTimer;
    
    // In development, React StrictMode may call this twice
    // Check if we already have an active connection to avoid duplicates
    if (enabled) {
      // Always attempt connection on mount or when re-enabled
      if (!wsRef.current || wsRef.current.readyState === WebSocket.CLOSED || wsRef.current.readyState === WebSocket.CLOSING) {
        console.log('🔌 WebSocket enabled - connecting...');
        // Small delay to ensure component is fully mounted
        connectTimer = setTimeout(() => {
          connect();
        }, 100);
      }
    } else {
      console.log('🔌 WebSocket disabled - disconnecting...');
      disconnect();
    }
    
    // Cleanup on unmount
    return () => {
      if (connectTimer) {
        clearTimeout(connectTimer);
      }
      
      // Don't set manual close flag on unmount/page reload
      // This allows reconnection after page reload
      if (wsRef.current) {
        console.log('🔌 Cleanup: closing WebSocket without preventing reconnection');
        wsRef.current.close(1000, 'Page unload');
      }
      
      // Clear all timeouts and intervals
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }
      
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
    };
  }, [enabled, connect, disconnect]);
  
  // Log connection status changes for debugging
  useEffect(() => {
    if (connectionStatus === 'connecting' || connectionStatus === 'connected') {
      // Add a small delay to log to ensure wsRef is updated
      setTimeout(() => {
        console.log('🔌 Connection status:', connectionStatus, 'wsRef exists:', !!wsRef.current, 'readyState:', wsRef.current?.readyState, 'enabled:', enabled);
      }, 10);
    }
  }, [connectionStatus, enabled]);
  
  // Monitor for stuck connections. Covers 'connecting' AND 'reconnecting'
  // (a reconnect whose connect() early-returned used to sit in 'reconnecting'
  // forever with no timer armed). No max-attempts gate — retry-forever policy.
  useEffect(() => {
    if (connectionStatus === 'connecting' || connectionStatus === 'reconnecting') {
      const stuckTimeout = setTimeout(() => {
        const stillStuck = (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN);
        if (stillStuck && !reconnectTimeoutRef.current) {
          console.warn(`⚠️ Connection stuck in ${connectionStatus} state - forcing reconnect`);
          setConnectionStatus('disconnected');
          attemptReconnect();
        }
      }, 45000); // must exceed the 30s backoff ceiling or it double-fires

      return () => clearTimeout(stuckTimeout);
    }
  }, [connectionStatus, attemptReconnect]);
  
  // Get subscription statistics
  const getSubscriptionStats = useCallback(() => {
    const subs = Array.from(subscriptionsRef.current.values());
    return {
      total: subs.length,
      byType: subs.reduce((acc, sub) => {
        acc[sub.type] = (acc[sub.type] || 0) + 1;
        return acc;
      }, {}),
      oldestSubscription: subs.length > 0 ? Math.min(...subs.map(s => s.createdAt)) : null,
      lastUpdate: subs.length > 0 ? Math.max(...subs.map(s => s.lastUpdate || 0)) : null
    };
  }, []);
  
  // Force reconnect function for manual reconnection
  const forceReconnect = useCallback(() => {
    console.log('🔄 Force reconnect requested');
    // Reset reconnect attempts
    reconnectAttemptsRef.current = 0;
    // Clear any pending timeouts
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    // Disconnect current connection
    if (wsRef.current) {
      isManualCloseRef.current = true;
      wsRef.current.close();
      wsRef.current = null;
    }
    // Reset state
    setConnectionStatus('disconnected');
    setError(null);
    // Clear the tracker's rapid-reconnect guard — without this the 100ms-later
    // connect() landed inside the tracker's 1s window and silently no-oped
    // (the "reconnect button does nothing" bug).
    wsConnectionTracker.reset();
    // Trigger reconnect after a brief delay
    setTimeout(() => {
      isManualCloseRef.current = false;
      connect();
    }, 100);
  }, [connect]);

  return {
    isConnected,
    connectionStatus,
    connectionQuality,
    error,
    connectedClients,
    ownClients,
    subscribeToEvents,
    subscribeToEventDetail,
    subscribeToMetaCampaigns,
    disconnect,
    reconnect: forceReconnect,
    getSubscriptionStats,
    // Expose connection metrics
    reconnectAttempts: reconnectAttemptsRef.current,
    subscriptionCount: subscriptionsRef.current.size
  };
};